import React, { useEffect, useMemo, useState } from "react";
import SearchableSelect from "./SearchableSelect";

const ROLE_OPTIONS = [
  { key: "sakha_id", label: "Self" },
  { key: "dadisa_sakha_id", label: "Dadisa" },
  { key: "nanisa_sakha_id", label: "Nanisa" },
  { key: "mamasa_sakha_id", label: "Mamasa" },
];

const C = {
  card: "#ffffff",
  border: "rgba(185,52,58,0.16)",
  borderStrong: "rgba(185,52,58,0.24)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  text: "#252935",
  muted: "#6b6f7d",
  mutedSoft: "#93808a",
  soft: "#fff7f5",
  softMuted: "#f6d8d2",
};

export default function SakhaAssignmentsField({
  options = [],
  values,
  onChange,
  disabled = false,
}) {
  const normalizedOptions = useMemo(() => (Array.isArray(options) ? options : []), [options]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedSakhaId, setSelectedSakhaId] = useState("");

  const optionMap = useMemo(() => {
    const map = new Map();
    normalizedOptions.forEach((option) => {
      map.set(String(option.id), option.label);
    });
    return map;
  }, [normalizedOptions]);

  const assignments = useMemo(
    () =>
      ROLE_OPTIONS.map((role) => ({
        ...role,
        value: String(values?.[role.key] || ""),
        sakhaName: optionMap.get(String(values?.[role.key] || "")) || "",
      })).filter((role) => role.value && role.sakhaName),
    [optionMap, values]
  );
  const selectedRoleOptions = useMemo(() => {
    const assignedRoleKeys = new Set(assignments.map((assignment) => assignment.key));
    return ROLE_OPTIONS
      .filter((role) => !assignedRoleKeys.has(role.key) || role.key === selectedRole)
      .map((role) => ({ id: role.key, label: role.label }));
  }, [assignments, selectedRole]);

  const currentAssignment = selectedRole ? String(values?.[selectedRole] || "") : "";
  const actionLabel = currentAssignment ? "Update selection" : "Add selection";
  const emptyStateMessage = !normalizedOptions.length
    ? "Select caste first to manage sakha assignments."
    : disabled
      ? "Sakha assignments are read-only right now."
      : "No sakha assignments added yet.";

  useEffect(() => {
    if (!selectedSakhaId) return;
    if (!optionMap.has(String(selectedSakhaId))) {
      setSelectedSakhaId("");
    }
  }, [optionMap, selectedSakhaId]);

  const applySelection = () => {
    if (!selectedRole || !selectedSakhaId) return;
    onChange?.(selectedRole, String(selectedSakhaId));
    setSelectedRole("");
    setSelectedSakhaId("");
  };

  const clearSelection = (roleKey) => {
    onChange?.(roleKey, "");
    if (selectedRole === roleKey) {
      setSelectedRole("");
      setSelectedSakhaId("");
    }
  };

  const editSelection = (roleKey, sakhaId) => {
    setSelectedRole(roleKey);
    setSelectedSakhaId(String(sakhaId || ""));
  };

  return (
    <div style={{ ...styles.wrap, ...(disabled ? styles.wrapDisabled : {}) }}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Sakha Assignments</div>
          <div style={styles.subTitle}>
            Choose a relation, add a sakha, and review the saved selections below.
          </div>
        </div>
      </div>

      <div style={styles.toolbar}>
        <SearchableSelect
          value={selectedRole}
          options={selectedRoleOptions}
          onChange={setSelectedRole}
          placeholder="Select relation"
          disabled={disabled}
          fieldShellStyle={styles.select}
          fieldShellDisabledStyle={styles.selectDisabled}
          inputStyle={styles.selectInput}
          chevronButtonStyle={styles.selectChevronButton}
          menuStyle={styles.selectMenu}
          optionStyle={styles.selectOption}
          optionActiveStyle={styles.selectOptionActive}
        />

        <SearchableSelect
          value={selectedSakhaId}
          options={normalizedOptions}
          onChange={setSelectedSakhaId}
          placeholder={normalizedOptions.length ? "Select sakha" : "Select caste first"}
          disabled={disabled || normalizedOptions.length === 0}
          fieldShellStyle={styles.select}
          fieldShellDisabledStyle={styles.selectDisabled}
          inputStyle={styles.selectInput}
          chevronButtonStyle={styles.selectChevronButton}
          menuStyle={styles.selectMenu}
          optionStyle={styles.selectOption}
          optionActiveStyle={styles.selectOptionActive}
        />

        <button
          type="button"
          style={{
            ...styles.actionBtn,
            ...((disabled || !selectedRole || !selectedSakhaId) ? styles.actionBtnDisabled : {}),
          }}
          onClick={applySelection}
          disabled={disabled || !selectedRole || !selectedSakhaId}
        >
          {actionLabel}
        </button>
      </div>

      {assignments.length > 0 ? (
        <div style={styles.assignmentList}>
          {assignments.map((assignment) => (
            <div key={assignment.key} style={styles.assignmentCard}>
              <div>
                <div style={styles.assignmentRole}>{assignment.label}</div>
                <div style={styles.assignmentName}>{assignment.sakhaName}</div>
              </div>

              <div style={styles.assignmentActions}>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={() => editSelection(assignment.key, assignment.value)}
                  disabled={disabled}
                >
                  Edit
                </button>
                <button
                  type="button"
                  style={styles.clearBtn}
                  onClick={() => clearSelection(assignment.key)}
                  disabled={disabled}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.emptyState}>{emptyStateMessage}</div>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    background: "linear-gradient(180deg, #ffffff 0%, #fff7f5 100%)",
    padding: 16,
    boxShadow: "0 14px 34px rgba(37,41,53,0.07)",
  },
  wrapDisabled: {
    opacity: 0.82,
  },
  header: {
    marginBottom: 14,
  },
  title: {
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: C.accentDark,
    marginBottom: 6,
  },
  subTitle: {
    fontSize: 13,
    lineHeight: 1.5,
    color: C.muted,
  },
  toolbar: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    alignItems: "center",
    marginBottom: 16,
  },
  select: {
    width: "100%",
    minHeight: 48,
    borderRadius: 16,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    fontSize: 16,
    color: C.text,
    outline: "none",
    boxSizing: "border-box",
  },
  selectDisabled: {
    background: C.soft,
    color: C.mutedSoft,
  },
  selectInput: {
    minHeight: 48,
    padding: "0 14px",
    fontSize: 16,
    color: C.text,
  },
  selectChevronButton: {
    height: 48,
    color: C.muted,
  },
  selectMenu: {
    borderRadius: 18,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
  },
  selectOption: {
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 14,
  },
  selectOptionActive: {
    borderColor: C.accent,
    background: C.accentLight,
    color: C.accentDark,
  },
  actionBtn: {
    width: "100%",
    minHeight: 48,
    border: "none",
    borderRadius: 16,
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: C.card,
    fontWeight: 700,
    padding: "0 18px",
    cursor: "pointer",
  },
  actionBtnDisabled: {
    background: C.softMuted,
    cursor: "not-allowed",
  },
  assignmentList: {
    display: "grid",
    gap: 10,
  },
  assignmentCard: {
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    background: C.card,
    padding: "14px 16px",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  assignmentRole: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: C.accentDark,
    marginBottom: 4,
  },
  assignmentName: {
    fontSize: 16,
    fontWeight: 700,
    color: C.text,
  },
  assignmentActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  secondaryBtn: {
    minHeight: 38,
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: C.accentLight,
    color: C.accentDark,
    padding: "0 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  clearBtn: {
    minHeight: 38,
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    background: C.card,
    color: C.muted,
    padding: "0 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  emptyState: {
    border: `1px dashed ${C.borderStrong}`,
    borderRadius: 18,
    background: C.soft,
    color: C.muted,
    padding: "14px 16px",
    fontSize: 14,
  },
};
